// Canonical ISCC / Sustainability standard detection.
//
// This lived as the same regex copy-pasted into three files (auditOutputTemplateData,
// documentFormValidationUtils, DocumentFormModal), which had already drifted; it is
// defined once here and imported everywhere. No other imports, so the cheap validation
// paths can use it without pulling in the PDF stack.
//
// Ground truth: CRM stores Sustainability standards as BARE SHORT LABELS -- "EU",
// "PLUS", "CORSIA", "CFC", "Japan-FIT" -- not as long names containing "ISCC"
// (see CBQAGLOBAL-CRM-BACKEND scripts/seed-audit-scenarios.sh). Anything matching on
// the word "ISCC" alone will miss real production data.

const readStandardText = (value) => String(
  typeof value === 'string'
    ? value
    : value?.standardName || value?.standard_name || value?.name || value?.label || value?.value || '',
).trim();

// Labels that contain an ISCC-ish word but are a different scheme entirely.
// "EU Ecolabel" is not ISCC EU, and ISO 20121 is "Event sustainability management
// systems" -- the previous bare /\bsustainability\b/ and /\beu\b/ tokens matched both.
const NOT_ISCC = /\biso\b|\biec\b|\becolabel\b/i;

// "Japan-FIT" is the stored label. The previous /\bjapan\s*fit\b/ used \s*, which does
// not match the hyphen, so the real label never matched.
const JAPAN_FIT = /\bjapan[\s_-]*fit\b/i;

// PLUS is its own ISCC scheme and is stored bare. It must make isIsccStandard true on
// its own -- requiring an additional ISCC token here is what dropped Annex I rows for
// exactly the certificates Annex I exists for.
const PLUS = /\bplus\b/i;

export const isIsccStandard = (value) => {
  const text = readStandardText(value);
  if (!text) return false;
  // An explicit brand mention always wins, even alongside an excluded word.
  if (/\biscc\b/i.test(text)) return true;
  if (NOT_ISCC.test(text)) return false;
  return PLUS.test(text)
    || /\bcorsia\b/i.test(text)
    || /\bcfc\b/i.test(text)
    || JAPAN_FIT.test(text)
    || /\bsustainability\b/i.test(text)
    // Only the bare "EU" service label, or "EU" as a path segment in a CRM service
    // name such as "Sustainability / EU / Certification".
    || /(^|\s\/\s)eu($|\s\/\s)/i.test(text);
};

// Mirrors the backend's resolveIsccTemplateAsset, which picks BLANK-ISCC-plus.pdf on a
// bare contains("PLUS"). Annex I applies exactly when that template is used, so the two
// tests must agree.
export const isIsccPlusStandard = (value) => {
  const text = readStandardText(value);
  if (!text || NOT_ISCC.test(text)) return false;
  return PLUS.test(text);
};
